/**
 * Workflows area layout.
 *
 * Renders the persistent sub-nav tab bar (Overview / Definitions / Projects /
 * Executions / Topology / DLQ / Retries) above an <Outlet/> — the same pattern
 * as the myApp area (myapp.tsx). The Overview tab (the tile dashboard + stats
 * row) lives at workflows/index.tsx; every other tab is a nested route under
 * workflows/.
 *
 * On the two authoring routes only (`AUTHORING_PATH`), the outer div is a
 * flex column filling `<main>` (AppLayout's own
 * `flex-1 overflow-y-auto`, which IS height-bounded), with the Outlet wrapper
 * a `flex-1 min-h-0` item. That gives the new-version/new authoring routes
 * (D18's page-fill technique, same as RelationshipModelPage.tsx) a DEFINITE
 * height to key off — the plain `space-y-4` div this replaces had no height
 * of its own, so the kit shell's `height: 100%` chain resolved against
 * "auto" and the canvas grew to fit the Library's full unscrolled content
 * (~2000px) instead of the viewport.
 *
 * `<main>` stays the scroll container, exactly as before: the wrapper sets
 * no overflow, so a long sub-page's content still extends `<main>`'s
 * scrollable area the way it always did. It must NOT be `overflow-y-auto`:
 * the authoring routes cancel `<main>`'s bottom padding with a negative
 * margin, and a clipping wrapper cut the shell's status bar off at the
 * wrapper's edge (measured 2026-10-11: text box at y 872–888, wrapper bottom
 * at 876 — the bar rendered blank in proofs P1–P4).
 */

import { createFileRoute, Outlet, useRouterState } from "@tanstack/react-router";

import { ProductSubNav } from "@/components/portal/ProductSubNav";
import { workflowsSubNav } from "@/lib/navigationItems";

export const Route = createFileRoute("/portal/workflows")({
  component: WorkflowsLayout,
});

/** The two full-page authoring routes (D18) — the only children that fill
 * `<main>` instead of flowing in it. */
const AUTHORING_PATH = /^\/portal\/workflows\/definitions\/(new|[^/]+\/new-version)\/?$/;

function WorkflowsLayout() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // Every other workflows page keeps the layout it has on origin/main: a
  // plain flow column, so a long page (definitions with 40 rows) still ends
  // with `<main>`'s bottom padding. Applying the h-full chain to them made
  // the column exactly `<main>`'s height and the overflowing rows ran into
  // that padding (measured gap 25px → 1px at 1440×900, gate S2).
  if (!AUTHORING_PATH.test(pathname)) {
    return (
      <div className="space-y-4">
        <ProductSubNav items={workflowsSubNav} />
        <Outlet />
      </div>
    );
  }
  return (
    <div className="flex h-full min-h-0 flex-col gap-4">
      <ProductSubNav items={workflowsSubNav} />
      <div className="min-h-0 flex-1">
        <Outlet />
      </div>
    </div>
  );
}
